import { z } from 'zod';

import {
  auditFieldsSchema,
  entityIdSchema,
  isoDateSchema,
  isoDateTimeSchema,
  pageMetaSchema,
  paginationQuerySchema,
} from './common.js';
import { reservationStatusSchema } from './reservations.js';

export const stayStatusSchema = z.enum(['draft', 'planned', 'confirmed', 'cancelled', 'completed']);
export const mutableStayStatusSchema = z.enum(['draft', 'planned']);

const stayFieldsSchema = z.object({
  name: z.string().trim().min(1).max(200),
  type: z.string().trim().min(1).max(120),
  startsOn: isoDateSchema,
  endsOn: isoDateSchema,
  capacity: z.number().int().nonnegative().max(1_000),
  minimumParticipants: z.number().int().nonnegative().max(1_000),
});

export const stayInputSchema = stayFieldsSchema.extend({
  status: mutableStayStatusSchema.default('planned'),
});

export const stayUpdateSchema = stayFieldsSchema.partial().extend({
  revision: z.number().int().positive(),
});

export const staySchema = stayFieldsSchema.extend({
  id: entityIdSchema,
  publicRef: z.string().trim().min(1).max(200),
  status: stayStatusSchema,
  confirmedAt: isoDateTimeSchema.nullable(),
  confirmedBy: entityIdSchema.nullable(),
  cancelledAt: isoDateTimeSchema.nullable(),
  cancelledBy: entityIdSchema.nullable(),
  cancellationReason: z.string().max(2_000).nullable(),
  archived: z.boolean(),
  ...auditFieldsSchema.shape,
});

export const stayWarningSchema = z.object({
  code: z.enum(['STAY_UNDER_MINIMUM', 'STAY_J2_UNCONFIRMED', 'STAY_PAST_UNCONFIRMED']),
  level: z.enum(['info', 'warning']),
  message: z.string().min(1),
  asOfDate: isoDateSchema,
  deadline: isoDateSchema.nullable(),
});

export const stayCapacitySchema = z.object({
  stayId: entityIdSchema,
  capacity: z.number().int().nonnegative(),
  confirmedParticipants: z.number().int().nonnegative(),
  activeOptionParticipants: z.number().int().nonnegative(),
  waitlistedParticipants: z.number().int().nonnegative(),
  available: z.number().int().nonnegative(),
});

export const staySummarySchema = stayCapacitySchema.extend({
  activeParticipants: z.number().int().nonnegative(),
  minimumParticipants: z.number().int().nonnegative(),
  underMinimum: z.boolean(),
  confirmed: z.boolean(),
  j2Deadline: isoDateSchema,
});

export const stayListItemSchema = staySchema.extend({
  summary: staySummarySchema,
  warnings: z.array(stayWarningSchema),
});

export const stayKpisSchema = z.object({
  upcoming: z.number().int().nonnegative(),
  underMinimum: z.number().int().nonnegative(),
  toConfirm: z.number().int().nonnegative(),
  cancelledInPeriod: z.number().int().nonnegative(),
});

export const stayPageSchema = z.object({
  items: z.array(stayListItemSchema),
  page: pageMetaSchema,
  kpis: stayKpisSchema,
});

const stayListCursorPayloadSchema = z
  .object({
    startsOn: isoDateSchema,
    id: entityIdSchema,
  })
  .strict();

const base64UrlAlphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

function encodeBase64Url(bytes: Uint8Array): string {
  let encoded = '';
  for (let index = 0; index < bytes.length; index += 3) {
    const first = bytes[index] ?? 0;
    const second = bytes[index + 1];
    const third = bytes[index + 2];
    const value = (first << 16) | ((second ?? 0) << 8) | (third ?? 0);
    encoded += base64UrlAlphabet[(value >>> 18) & 63] ?? '';
    encoded += base64UrlAlphabet[(value >>> 12) & 63] ?? '';
    if (second !== undefined) encoded += base64UrlAlphabet[(value >>> 6) & 63] ?? '';
    if (third !== undefined) encoded += base64UrlAlphabet[value & 63] ?? '';
  }
  return encoded;
}

function decodeBase64Url(value: string): Uint8Array | null {
  if (!/^[A-Za-z0-9_-]+$/u.test(value) || value.length % 4 === 1) return null;
  const bytes: number[] = [];
  let buffer = 0;
  let bitCount = 0;
  for (const character of value) {
    const digit = base64UrlAlphabet.indexOf(character);
    if (digit < 0) return null;
    buffer = (buffer << 6) | digit;
    bitCount += 6;
    if (bitCount >= 8) {
      bitCount -= 8;
      bytes.push((buffer >>> bitCount) & 255);
      buffer &= (1 << bitCount) - 1;
    }
  }
  if (buffer !== 0) return null;
  return Uint8Array.from(bytes);
}

function encodeUtf8(value: string): Uint8Array {
  const encoded = encodeURIComponent(value);
  const bytes: number[] = [];
  for (let index = 0; index < encoded.length; index += 1) {
    if (encoded[index] === '%') {
      bytes.push(Number.parseInt(encoded.slice(index + 1, index + 3), 16));
      index += 2;
    } else {
      bytes.push(encoded.charCodeAt(index));
    }
  }
  return Uint8Array.from(bytes);
}

function decodeUtf8(bytes: Uint8Array): string | null {
  try {
    const encoded = [...bytes].map((byte) => `%${byte.toString(16).padStart(2, '0')}`).join('');
    return decodeURIComponent(encoded);
  } catch {
    return null;
  }
}

export type StayListCursor = z.infer<typeof stayListCursorPayloadSchema>;

export function encodeStayListCursor(cursor: StayListCursor): string {
  const parsed = stayListCursorPayloadSchema.parse(cursor);
  return encodeBase64Url(encodeUtf8(JSON.stringify(parsed)));
}

export function decodeStayListCursor(cursor: string): StayListCursor | null {
  const bytes = decodeBase64Url(cursor);
  if (bytes === null) return null;
  const decoded = decodeUtf8(bytes);
  if (decoded === null) return null;
  try {
    const value: unknown = JSON.parse(decoded);
    const parsed = stayListCursorPayloadSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export const stayListCursorSchema = z
  .string()
  .trim()
  .min(1)
  .refine((cursor) => decodeStayListCursor(cursor) !== null, {
    message: 'Le curseur de pagination Séjours est invalide.',
  });

export const stayListQuerySchema = paginationQuerySchema.extend({
  cursor: stayListCursorSchema.optional(),
  type: z.string().trim().max(120).optional(),
  status: stayStatusSchema.optional(),
  startsFrom: isoDateSchema.optional(),
  startsTo: isoDateSchema.optional(),
});

export const stayLinkedReservationSchema = z.object({
  id: entityIdSchema,
  publicRef: z.string().min(1),
  customerId: entityIdSchema.nullable(),
  customerName: z.string().min(1),
  status: reservationStatusSchema,
  participantCount: z.number().int().nonnegative(),
  revision: z.number().int().positive(),
  createdAt: isoDateTimeSchema,
});

export const waitlistEntrySchema = stayLinkedReservationSchema.extend({
  status: z.literal('waitlisted'),
  waitlistedAt: isoDateTimeSchema,
});

export const stayHistoryEntrySchema = z.object({
  id: entityIdSchema,
  action: z.string().min(1),
  actorId: entityIdSchema.nullable(),
  occurredAt: isoDateTimeSchema,
  detail: z.string(),
});

export const stayDetailSchema = staySchema.extend({
  summary: staySummarySchema,
  warnings: z.array(stayWarningSchema),
  reservations: z.array(stayLinkedReservationSchema),
  waitlist: z.array(waitlistEntrySchema),
  history: z.array(stayHistoryEntrySchema),
});

export const stayConfirmationSchema = z.object({
  revision: z.number().int().positive(),
});

export const stayCancellationSchema = z.object({
  revision: z.number().int().positive(),
  reason: z.string().trim().min(1).max(2_000),
});

export const stayDeferredConsequenceSchema = z.object({
  code: z.literal('RESERVATION_STATUS_REVIEW_REQUIRED'),
  message: z.string().min(1),
  reservationCount: z.number().int().nonnegative(),
});

export const stayCancellationResultSchema = z.object({
  stay: stayDetailSchema,
  deferredConsequences: z.array(stayDeferredConsequenceSchema),
});

export const waitlistPromotionSchema = z.object({
  revision: z.number().int().positive(),
});

export const closedPeriodInputSchema = z.object({
  startsOn: isoDateSchema,
  endsOn: isoDateSchema,
  comment: z.string().trim().min(1).max(2_000),
});

export const closedPeriodUpdateSchema = closedPeriodInputSchema.partial().extend({
  revision: z.number().int().positive(),
});

export const closedPeriodArchiveSchema = z.object({
  revision: z.number().int().positive(),
});

export const closedPeriodSchema = closedPeriodInputSchema.extend({
  id: entityIdSchema,
  active: z.boolean(),
  archivedAt: isoDateTimeSchema.nullable(),
  archivedBy: entityIdSchema.nullable(),
  ...auditFieldsSchema.shape,
});

export const closedPeriodListSchema = z.array(closedPeriodSchema);

const monthSchema = z.string().regex(/^\d{4}-(?:0[1-9]|1[0-2])$/u);

export const businessContextSchema = z.object({
  businessToday: isoDateSchema,
  timeZone: z.literal('America/Cayenne'),
});

export const planningQuerySchema = z.object({
  month: monthSchema,
  type: z.string().trim().max(120).optional(),
  status: stayStatusSchema.optional(),
});

export const planningStaySchema = z.object({
  id: entityIdSchema,
  publicRef: z.string().min(1),
  name: z.string().min(1),
  type: z.string().min(1),
  startsOn: isoDateSchema,
  endsOn: isoDateSchema,
  status: stayStatusSchema,
  activeParticipants: z.number().int().nonnegative(),
  waitlistedParticipants: z.number().int().nonnegative(),
  available: z.number().int().nonnegative(),
  warnings: z.array(stayWarningSchema),
});

export const planningMonthSchema = z
  .object({
    month: monthSchema,
    startsOn: isoDateSchema,
    endsOn: isoDateSchema,
    stays: z.array(planningStaySchema),
    closedPeriods: z.array(closedPeriodSchema),
    counters: z.object({
      stays: z.number().int().nonnegative(),
      confirmed: z.number().int().nonnegative(),
      planned: z.number().int().nonnegative(),
      cancelled: z.number().int().nonnegative(),
      activeParticipants: z.number().int().nonnegative(),
      waitlistedParticipants: z.number().int().nonnegative(),
    }),
  })
  .superRefine((value, context) => {
    const seenStayIds = new Map<string, number>();
    value.stays.forEach((stay, index) => {
      const firstIndex = seenStayIds.get(stay.id);
      if (firstIndex !== undefined) {
        context.addIssue({
          code: 'custom',
          path: ['stays', index, 'id'],
          message: `Le Séjour est déjà présent à l’index ${firstIndex}.`,
        });
      } else {
        seenStayIds.set(stay.id, index);
      }
      if (stay.endsOn < value.startsOn || stay.startsOn > value.endsOn) {
        context.addIssue({
          code: 'custom',
          path: ['stays', index],
          message: 'Le Séjour ne recouvre pas le mois demandé.',
        });
      }
    });

    const expectedCounters = {
      stays: value.stays.length,
      confirmed: value.stays.filter((stay) => stay.status === 'confirmed').length,
      planned: value.stays.filter((stay) => stay.status === 'planned').length,
      cancelled: value.stays.filter((stay) => stay.status === 'cancelled').length,
      activeParticipants: value.stays.reduce((total, stay) => total + stay.activeParticipants, 0),
      waitlistedParticipants: value.stays.reduce(
        (total, stay) => total + stay.waitlistedParticipants,
        0,
      ),
    };
    for (const [counter, expected] of Object.entries(expectedCounters)) {
      if (value.counters[counter as keyof typeof expectedCounters] !== expected) {
        context.addIssue({
          code: 'custom',
          path: ['counters', counter],
          message: `Le compteur doit être égal à ${expected}.`,
        });
      }
    }
  });

export type Stay = z.infer<typeof staySchema>;
export type StayInput = z.infer<typeof stayInputSchema>;
export type StayUpdate = z.infer<typeof stayUpdateSchema>;
export type StayStatus = z.infer<typeof stayStatusSchema>;
export type StayListItem = z.infer<typeof stayListItemSchema>;
export type StayDetail = z.infer<typeof stayDetailSchema>;
export type StayCancellationResult = z.infer<typeof stayCancellationResultSchema>;
export type StaySummary = z.infer<typeof staySummarySchema>;
export type StayCapacity = z.infer<typeof stayCapacitySchema>;
export type StayWarning = z.infer<typeof stayWarningSchema>;
export type StayKpis = z.infer<typeof stayKpisSchema>;
export type StayListQuery = z.infer<typeof stayListQuerySchema>;
export type ClosedPeriod = z.infer<typeof closedPeriodSchema>;
export type ClosedPeriodInput = z.infer<typeof closedPeriodInputSchema>;
export type ClosedPeriodUpdate = z.infer<typeof closedPeriodUpdateSchema>;
export type PlanningMonth = z.infer<typeof planningMonthSchema>;
export type PlanningQuery = z.infer<typeof planningQuerySchema>;
export type PlanningStay = z.infer<typeof planningStaySchema>;
export type BusinessContext = z.infer<typeof businessContextSchema>;
export type WaitlistEntry = z.infer<typeof waitlistEntrySchema>;
